import type { BusinessWorkspaceState } from './workbench-workspace-state';

const DEFAULT_LIST_PATH = '/item/sellable';

function normalizePathname(pathname: string): string {
  if (!pathname) {
    return DEFAULT_LIST_PATH;
  }
  const withoutQuery = pathname.split('?')[0] ?? pathname;
  const normalized = withoutQuery.replace(/\/{2,}/g, '/').replace(/\/$/, '');
  return normalized === '' ? '/' : normalized;
}

function parseSearchParams(search?: URLSearchParams | string): URLSearchParams | undefined {
  if (search instanceof URLSearchParams) {
    return search;
  }
  if (typeof search === 'string' && search.trim().length > 0) {
    const normalized = search.startsWith('?') ? search.slice(1) : search;
    return new URLSearchParams(normalized);
  }
  return undefined;
}

/** P8-2 — hyphenated item subdomains (e.g. raw-material) → BusinessDomain SSOT */
export function resolveItemSubdomainBusinessDomain(domain: string, subdomain: string): string {
  if (domain === 'entity') {
    const entitySubdomainDomains: Record<string, string> = {
      internal: 'internal_entity',
      external: 'external_entity',
      supplier: 'supplier',
      user: 'user_entity',
    };
    const mapped = entitySubdomainDomains[subdomain];
    if (mapped) return mapped;
    return `${subdomain.replace(/-/g, '_')}_entity`;
  }
  if (domain === 'item') {
    const itemSubdomainDomains: Record<string, string> = {
      sellable: 'sellable_item',
      'raw-material': 'raw_material_item',
      service: 'service_item',
      asset: 'asset_item',
      consumable: 'consumable_item',
    };
    const mapped = itemSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}_item`;
  }
  if (domain === 'manufacturing') {
    const manufacturingSubdomainDomains: Record<string, string> = {
      bom: 'manufacturing_bom',
      'production-order': 'production_order',
      'quality-inspection': 'quality_inspection',
    };
    const mapped = manufacturingSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'finance') {
    const financeSubdomainDomains: Record<string, string> = {
      state: 'finance_state',
      flow: 'finance_flow',
      budget: 'finance_budget',
      vouchers: 'stream_execution',
      receivables: 'finance_state',
      overdue: 'finance_state',
    };
    const mapped = financeSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'finance-advanced') {
    const financeAdvancedSubdomainDomains: Record<string, string> = {
      'legal-entity': 'legal_entity',
      'currency-rate': 'currency_rate',
      intercompany: 'intercompany',
    };
    const mapped = financeAdvancedSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'space') {
    const spaceSubdomainDomains: Record<string, string> = {
      physical: 'physical_space',
      virtual: 'virtual_space',
    };
    const mapped = spaceSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}_space`;
  }
  if (domain === 'knowledge') {
    const knowledgeSubdomainDomains: Record<string, string> = {
      report: 'knowledge_report',
      policy: 'knowledge_policy',
      plan: 'knowledge_plan',
    };
    const mapped = knowledgeSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `knowledge_${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'appeal') {
    const appealSubdomainDomains: Record<string, string> = {
      content: 'semantic_formation',
      'semantic-formation': 'semantic_formation',
      'ephemeral-formation': 'ephemeral_formation',
      'ephemeral-performance': 'ephemeral_performance',
      'recurrent-performance': 'recurrent_performance',
    };
    const mapped = appealSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'calendar') {
    const calendarSubdomainDomains: Record<string, string> = {
      event: 'calendar_anchor',
      trigger: 'calendar_trigger',
      cycle: 'calendar_cycle',
    };
    const mapped = calendarSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `calendar_${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'event') {
    const eventSubdomainDomains: Record<string, string> = {
      'flow-definition': 'flow_definition',
      'stream-definition': 'stream_definition',
      'action-binding': 'action_binding',
      'flow-execution': 'flow_execution',
      'stream-execution': 'stream_execution',
      'flow-event': 'flow_event',
      'stream-event': 'stream_event',
    };
    const mapped = eventSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'log') {
    const logSubdomainDomains: Record<string, string> = {
      flow: 'flow_event',
      stream: 'stream_event',
    };
    const mapped = logSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'warehouse') {
    const warehouseSubdomainDomains: Record<string, string> = {
      'picking-list': 'warehouse_picking_list',
      receiving: 'warehouse_receiving',
      putaway: 'warehouse_putaway',
      'ops-board': 'warehouse_ops_board',
    };
    const mapped = warehouseSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'inventory') {
    const inventorySubdomainDomains: Record<string, string> = {
      shortage: 'inventory_shortage',
      stock: 'inventory_stock',
      movements: 'inventory_movement',
      adjustments: 'inventory_adjustment',
    };
    const mapped = inventorySubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'workspace') {
    const workspaceSubdomainDomains: Record<string, string> = {
      operations: 'workspace_operation',
      tasks: 'task_execution',
    };
    const mapped = workspaceSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  if (domain === 'flow') {
    const flowSubdomainDomains: Record<string, string> = {
      streams: 'stream_definition',
      assignments: 'assignment_execution',
      executions: 'flow_execution',
    };
    const mapped = flowSubdomainDomains[subdomain];
    if (mapped) {
      return mapped;
    }
    return `${subdomain.replace(/-/g, '_')}`;
  }
  return `${subdomain.replace(/-/g, '_')}_item`;
}

/** Finance voucher create route — `/finance/voucher/:streamType/new` */
export const FINANCE_VOUCHER_ENTRY_ROUTE_PATTERN = /^\/finance\/voucher\/([^/]+)\/new$/;

/** Flow stream execute route — `/flow/streams/:id/execute` (4-segment non-standard detail). */
export const FLOW_STREAM_EXECUTE_ROUTE_PATTERN = /^\/flow\/streams\/([^/]+)\/execute$/;

/** Workspace operation executor — `/workspace/operations/:id` */
export const WORKSPACE_OPERATION_ROUTE_PATTERN = /^\/workspace\/operations\/([^/]+)$/;

/** Approval detail — `/approvals/:assignmentId` (excludes pending/history utility routes). */
export const APPROVAL_DETAIL_ROUTE_PATTERN = /^\/approvals\/([^/]+)$/;

const APPROVAL_DETAIL_RESERVED_SEGMENTS = new Set(['pending', 'history']);

const UTILITY_PAGE_ROUTE_CONFIG: Record<string, { domain: string; subdomain?: string }> = {
  '/approvals/pending': { domain: 'approvals', subdomain: 'pending' },
  '/approvals/history': { domain: 'approvals', subdomain: 'history' },
  '/automation': { domain: 'automation' },
  '/export': { domain: 'export' },
  '/appeal/campaign': { domain: 'appeal', subdomain: 'campaign' },
};

const SYSTEM_SCHEMA_CREATE_PATH = '/system/schema/create' as const;

const SYSTEM_ADMIN_LIST_ROUTE_CONFIG: Record<string, { subdomain: string }> = {
  '/system/audit': { subdomain: 'audit' },
  '/system/site-join-applications': { subdomain: 'site-join-applications' },
  '/system/site-readiness': { subdomain: 'site-readiness' },
  '/system/tenant-genesis': { subdomain: 'tenant-genesis' },
  '/system/configuration-workbench': { subdomain: 'configuration-workbench' },
  '/system/pilot-role-surface': { subdomain: 'pilot-role-surface' },
  '/system/pilot-field-surface': { subdomain: 'pilot-field-surface' },
  '/system/pilot-operator-support': { subdomain: 'pilot-operator-support' },
  '/system/adoption-status': { subdomain: 'adoption-status' },
  '/system/flow-surface-composition-proof': { subdomain: 'flow-surface-composition-proof' },
  '/system/remaining-operational-flow-adoption-proof': {
    subdomain: 'remaining-operational-flow-adoption-proof',
  },
  '/system/produce-inventory-flow-adoption-proof': {
    subdomain: 'produce-inventory-flow-adoption-proof',
  },
  '/system/object-scan-flow-adoption-proof': {
    subdomain: 'object-scan-flow-adoption-proof',
  },
  '/system/operation-composition-builder': {
    subdomain: 'operation-composition-builder',
  },
  '/system/document-review/new': { subdomain: 'document-review/new' },
  '/system/document-review/inbox': { subdomain: 'document-review/inbox' },
  '/system/document-review-support': { subdomain: 'document-review-support' },
};

function parseSystemWorkbenchLocation(pathname: string): BusinessWorkspaceState | null {
  const normalized = normalizePathname(pathname);
  if (normalized === SYSTEM_SCHEMA_CREATE_PATH) {
    return {
      plane: 'business',
      domain: 'system',
      subdomain: 'schema',
      surface: 'settings',
      selection: [],
      intent: 'general_browse',
      mode: 'view',
    };
  }
  const adminConfig = SYSTEM_ADMIN_LIST_ROUTE_CONFIG[normalized];
  if (adminConfig) {
    return {
      plane: 'business',
      domain: 'system',
      subdomain: adminConfig.subdomain,
      surface: 'admin_list',
      selection: [],
      intent: 'general_browse',
      mode: 'view',
    };
  }
  return null;
}

function resolveSystemWorkbenchPath(state: BusinessWorkspaceState): string | null {
  if (state.domain !== 'system') {
    return null;
  }
  if (state.surface === 'settings' && state.subdomain === 'schema') {
    return SYSTEM_SCHEMA_CREATE_PATH;
  }
  if (state.surface !== 'admin_list') {
    return null;
  }
  const subdomain = state.subdomain?.trim();
  if (!subdomain) {
    return null;
  }
  for (const [pathname, config] of Object.entries(SYSTEM_ADMIN_LIST_ROUTE_CONFIG)) {
    if (config.subdomain === subdomain) {
      return pathname;
    }
  }
  return null;
}

function parseUtilityPageLocation(pathname: string): BusinessWorkspaceState | null {
  const normalized = normalizePathname(pathname);
  const config = UTILITY_PAGE_ROUTE_CONFIG[normalized];
  if (!config) {
    return null;
  }
  return {
    plane: 'business',
    domain: config.domain,
    ...(config.subdomain ? { subdomain: config.subdomain } : {}),
    surface: 'utility_page',
    selection: [],
    intent: 'general_browse',
    mode: 'view',
  };
}

function resolveUtilityPagePath(state: BusinessWorkspaceState): string | null {
  if (state.surface !== 'utility_page') {
    return null;
  }
  const domain = state.domain?.trim();
  if (!domain) {
    return null;
  }
  const subdomain = state.subdomain?.trim();
  const key = subdomain ? `${domain}/${subdomain}` : domain;
  for (const [pathname, config] of Object.entries(UTILITY_PAGE_ROUTE_CONFIG)) {
    const configKey = config.subdomain ? `${config.domain}/${config.subdomain}` : config.domain;
    if (configKey === key) {
      return pathname;
    }
  }
  return null;
}

export function isFinanceVoucherEntryRoute(pathname: string): boolean {
  const normalized = (pathname.split('?')[0] ?? pathname).replace(/\/$/, '');
  return FINANCE_VOUCHER_ENTRY_ROUTE_PATTERN.test(normalized);
}

export function parseFinanceVoucherEntryStreamType(pathname: string): string | null {
  const normalized = (pathname.split('?')[0] ?? pathname).replace(/\/$/, '');
  const match = FINANCE_VOUCHER_ENTRY_ROUTE_PATTERN.exec(normalized);
  if (!match?.[1]) return null;
  const streamType = decodeURIComponent(match[1]).trim();
  return streamType.length > 0 ? streamType : null;
}

export function isFlowStreamExecuteRoute(pathname: string): boolean {
  const normalized = (pathname.split('?')[0] ?? pathname).replace(/\/$/, '');
  return FLOW_STREAM_EXECUTE_ROUTE_PATTERN.test(normalized);
}

export function parseFlowStreamExecuteId(pathname: string): string | null {
  const normalized = (pathname.split('?')[0] ?? pathname).replace(/\/$/, '');
  const match = FLOW_STREAM_EXECUTE_ROUTE_PATTERN.exec(normalized);
  if (!match?.[1]) return null;
  const streamId = decodeURIComponent(match[1]).trim();
  return streamId.length > 0 ? streamId : null;
}

export function parseWorkspaceOperationId(pathname: string): string | null {
  const normalized = (pathname.split('?')[0] ?? pathname).replace(/\/$/, '');
  const match = WORKSPACE_OPERATION_ROUTE_PATTERN.exec(normalized);
  if (!match?.[1]) return null;
  const operationId = decodeURIComponent(match[1]).trim();
  return operationId.length > 0 ? operationId : null;
}

export function parseApprovalAssignmentId(pathname: string): string | null {
  const normalized = (pathname.split('?')[0] ?? pathname).replace(/\/$/, '');
  const match = APPROVAL_DETAIL_ROUTE_PATTERN.exec(normalized);
  if (!match?.[1]) return null;
  const assignmentId = decodeURIComponent(match[1]).trim();
  if (!assignmentId || APPROVAL_DETAIL_RESERVED_SEGMENTS.has(assignmentId)) {
    return null;
  }
  return assignmentId;
}

function createListState(): BusinessWorkspaceState {
  return {
    plane: 'business',
    domain: 'item',
    subdomain: 'sellable',
    surface: 'list',
    selection: [],
    intent: 'general_browse',
    mode: 'view',
  };
}

export function parseWorkbenchLocation(
  pathname: string,
  search?: URLSearchParams | string
): BusinessWorkspaceState {
  const normalizedPath = normalizePathname(pathname);
  const systemWorkbenchState = parseSystemWorkbenchLocation(normalizedPath);
  if (systemWorkbenchState) {
    return systemWorkbenchState;
  }
  const utilityPageState = parseUtilityPageLocation(normalizedPath);
  if (utilityPageState) {
    return utilityPageState;
  }
  const streamExecuteId = parseFlowStreamExecuteId(normalizedPath);
  if (streamExecuteId) {
    const streamRoot = {
      id: streamExecuteId,
      businessDomain: 'stream_execution',
    };
    return {
      plane: 'business',
      domain: 'flow',
      subdomain: 'streams',
      surface: 'stream_execute',
      root: streamRoot,
      selection: [streamRoot],
      intent: 'general_browse',
      mode: 'view',
    };
  }

  const voucherStreamType = parseFinanceVoucherEntryStreamType(normalizedPath);
  if (voucherStreamType) {
    const voucherRoot = {
      id: voucherStreamType,
      businessDomain: 'stream_execution',
      label: voucherStreamType,
    };
    return {
      plane: 'business',
      domain: 'finance',
      subdomain: 'vouchers',
      surface: 'voucher_entry',
      root: voucherRoot,
      selection: [voucherRoot],
      intent: 'general_browse',
      mode: 'view',
    };
  }

  const operationId = parseWorkspaceOperationId(normalizedPath);
  if (operationId) {
    const operationRoot = {
      id: operationId,
      businessDomain: 'workspace_operation',
      label: operationId,
    };
    return {
      plane: 'business',
      domain: 'workspace',
      subdomain: 'operations',
      surface: 'operation_executor',
      root: operationRoot,
      selection: [operationRoot],
      intent: 'general_browse',
      mode: 'view',
    };
  }

  const approvalAssignmentId = parseApprovalAssignmentId(normalizedPath);
  if (approvalAssignmentId) {
    const approvalRoot = {
      id: approvalAssignmentId,
      businessDomain: 'flow_execution',
      label: approvalAssignmentId,
    };
    return {
      plane: 'business',
      domain: 'approvals',
      surface: 'approval_detail',
      root: approvalRoot,
      selection: [approvalRoot],
      intent: 'general_browse',
      mode: 'view',
    };
  }

  const listState = createListState();
  const params = parseSearchParams(search);
  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length >= 2) {
    const domain = segments[0];
    const subdomain = segments[1];
    if (domain) {
      listState.domain = domain;
    }
    if (subdomain) {
      listState.subdomain = subdomain;
    }
  }

  if (segments.length >= 3) {
    const rootId = decodeURIComponent(segments[2] ?? '');
    if (rootId.length > 0) {
      listState.surface = 'detail';
      listState.root = {
        id: rootId,
        businessDomain: resolveItemSubdomainBusinessDomain(
          listState.domain ?? 'item',
          listState.subdomain ?? 'sellable'
        ),
      };
      listState.selection = [listState.root];
    }
  }

  const pane = params?.get('pane')?.trim();
  if (pane) {
    listState.pane = pane;
  }
  const density = params?.get('density')?.trim();
  if (density) {
    listState.density = density;
  }
  const threadId = params?.get('threadId')?.trim();
  if (threadId) {
    listState.threadId = threadId;
  }
  const exceptionId = params?.get('exceptionId')?.trim();
  if (exceptionId) {
    listState.exceptionId = exceptionId;
  }

  return listState;
}

export function serializeWorkbenchLocation(state: BusinessWorkspaceState): {
  pathname: string;
  search?: string;
} {
  const domain = state.domain?.trim() || 'item';
  const subdomain = state.subdomain?.trim() || 'sellable';
  const rootId = state.root?.id?.trim();
  const systemWorkbenchPath = resolveSystemWorkbenchPath(state);
  const utilityPagePath = resolveUtilityPagePath(state);
  const isDetail = state.surface === 'detail' && Boolean(rootId);
  const isOperationExecutor = state.surface === 'operation_executor' && Boolean(rootId);
  const isApprovalDetail = state.surface === 'approval_detail' && Boolean(rootId);
  const isStreamExecute = state.surface === 'stream_execute' && Boolean(rootId);
  const pathname =
    systemWorkbenchPath ??
    utilityPagePath ??
    (isStreamExecute && rootId
      ? `/flow/streams/${encodeURIComponent(rootId)}/execute`
      : isOperationExecutor && rootId
        ? `/workspace/operations/${encodeURIComponent(rootId)}`
        : isApprovalDetail && rootId
          ? `/approvals/${encodeURIComponent(rootId)}`
          : isDetail && rootId
            ? `/${domain}/${subdomain}/${encodeURIComponent(rootId)}`
            : `/${domain}/${subdomain}`);

  const params = new URLSearchParams();
  if (state.pane) {
    params.set('pane', state.pane);
  }
  if (state.density) {
    params.set('density', state.density);
  }
  if (state.threadId) {
    params.set('threadId', state.threadId);
  }
  if (state.exceptionId) {
    params.set('exceptionId', state.exceptionId);
  }

  const query = params.toString();
  return query ? { pathname, search: `?${query}` } : { pathname };
}

export function resolveRouteFamilyFromState(state: BusinessWorkspaceState): string | null {
  if (!state.domain || !state.subdomain) {
    return null;
  }
  return `${state.domain}/${state.subdomain}`;
}
